import { IconDefinition } from '@fortawesome/fontawesome-common-types';
import { faMicrosoft } from '@fortawesome/free-brands-svg-icons';
import {
   faArrowAltFromLeft as faArrowAltFromLeftDuotone,
   faAssistiveListeningSystems as faAssistiveListeningSystemsDuotone,
   faCheck as faCheckDuotone,
   faComment as faCommentDuotone,
   faComments as faCommentsDuotone,
   faEnvelope,
   faExclamationTriangle as faExclamationTriangleDuotone,
   faFile,
   faFileAlt,
   faFileArchive,
   faFileAudio,
   faFileCertificate,
   faFileCode,
   faFileExcel,
   faFileImage,
   faFilePdf,
   faFilePowerpoint,
   faFileSpreadsheet,
   faFileVideo,
   faFileWord,
   faLock as faLockDuotone,
   faMusicAlt,
   faMusicAltSlash,
   faPauseCircle as faPauseCircleDuotone,
   faPowerOff as faPowerOffDuotone,
   faSave as faSaveDuotone,
   faSearch as faSearchDuotone,
   faSpinnerThird as faSpinnerThirdDuotone,
   faSync as faSyncDuotone,
   faTrash,
} from '@fortawesome/pro-duotone-svg-icons';
import { faStar as faStarLight } from '@fortawesome/pro-light-svg-icons';
import { faFilter as faFilterRegular } from '@fortawesome/pro-regular-svg-icons';
import {
   faArrowAltFromLeft as faArrowAltFromLeftSolid,
   faArrowRight as faArrowRightSolid,
   faAssistiveListeningSystems as faAssistiveListeningSystemsSolid,
   faCheck as faCheckSolid,
   faComment as faCommentSolid,
   faComments as faCommentsSolid,
   faEllipsisH as faEllipsisHSolid,
   faEnvelope as faEnvelopeSolid,
   faExclamationTriangle as faExclamationTriangleSolid,
   faFilter as faFilterSolid,
   faShare as faForwardSolid,
   faInboxIn as faInboxInSolid,
   faLock as faLockSolid,
   faMusicAltSlash as faMusicAltSlashSolid,
   faMusicAlt as faMusicAltSolid,
   faParking as faParkingSolid,
   faPauseCircle as faPauseCircleSolid,
   faPhoneAlt as faPhoneAltSolid,
   faPhone as faPhoneSolid,
   faPowerOff as faPowerOffSolid,
   faRecycle as faRecycleSolid,
   faReplyAll as faReplyAllSolid,
   faReply as faReplySolid,
   faRoute as faRouteSolid,
   faSave as faSaveSolid,
   faSearch as faSearchSolid,
   faSpinnerThird as faSpinnerThirdSolid,
   faStar as faStarSolid,
   faSync as faSyncSolid,
   faTimes as faTimesSolid,
   faUser as faUserSolid,
} from '@fortawesome/pro-solid-svg-icons';
import { Enum, EnumType } from 'ts-jenum';

// Provides icons for re-occurring items
// Each icon can provide a differing small icon to improve legibility
@Enum<WellKnownIconsEnum>('name')
export class WellKnownIconsEnum extends EnumType<WellKnownIconsEnum>() {
   //
   // --- Universal

   // For cancelling an action, e.g. closing a dialog without saving
   // Note: always a solid icon
   static readonly CANCEL = new WellKnownIconsEnum('Cancel', faTimesSolid, faTimesSolid);

   // For confirming an action, e.g. "OK" in a dialog
   // Note: always a solid icon
   static readonly CONFIRM = new WellKnownIconsEnum('Confirm', faCheckSolid, faCheckSolid);

   // For clearing input, e.g. in a search box
   // Note: always a solid icon
   static readonly CLEAR = new WellKnownIconsEnum('Clear', faTimesSolid, faTimesSolid);

   // For visually highlighting error information
   static readonly ERROR = new WellKnownIconsEnum('Error', faExclamationTriangleDuotone, faExclamationTriangleSolid);

   // For visually highlighting success information
   static readonly SUCCESS = new WellKnownIconsEnum('Success', faCheckDuotone, faCheckSolid);

   // To mark filter capabilities
   static readonly FILTER = new WellKnownIconsEnum('Filter', faFilterRegular, faFilterSolid);

   // For saving changes, settings, ...
   static readonly SAVE = new WellKnownIconsEnum('Save', faSaveDuotone, faSaveSolid);

   // To mark search capabilities
   static readonly SEARCH = new WellKnownIconsEnum('Search', faSearchDuotone, faSearchSolid);

   // Signing out of the application
   static readonly SIGN_OUT = new WellKnownIconsEnum('Sign_Out', faPowerOffDuotone, faPowerOffSolid);

   // Access to a resource, brick, feature, ... is locked (e.g. because of current license or permissions)
   static readonly NOT_ALLOWED = new WellKnownIconsEnum('NotAllowed', faLockDuotone, faLockSolid);

   // Access to a resource, brick, ... is not ready yet (e.g. because no connection to backend/core exists)
   static readonly NOT_READY = new WellKnownIconsEnum('NotReady', faSpinnerThirdDuotone, faSpinnerThirdSolid);

   // Resync (e.g. as updated data is available)
   static readonly RESYNC = new WellKnownIconsEnum('ReSync', faSyncDuotone, faSyncSolid);

   // Microsoft logo
   static readonly MICROSOFT = new WellKnownIconsEnum('Microsoft', faMicrosoft, faMicrosoft);

   //
   // --- Calling

   // Answering
   // Note: always a solid icon
   static readonly CALLING_ANSWER = new WellKnownIconsEnum('Calling_Answer', faPhoneAltSolid, faPhoneAltSolid);

   // Dialing Arrow
   // This arrow is meant to be combined with the CALLING_ANSWER icon to create a CALLING_DIAL icon
   // Note: always a solid icon
   static readonly CALLING_DIAL_ARROW = new WellKnownIconsEnum('Calling_Dial_Arrow', faArrowRightSolid, faArrowRightSolid);

   // User Decision
   // This icon is meant to be combined with the CALLING_ANSWER icon to create an icon when user decision is required
   // Note: always a solid icon
   static readonly CALLING_USER_DECISION = new WellKnownIconsEnum('Calling_User_Decision', faEllipsisHSolid, faEllipsisHSolid);

   // Hanging up
   // Note: always a solid icon
   static readonly CALLING_HANGUP = new WellKnownIconsEnum('Calling_Hangup', faPhoneSolid, faPhoneSolid);

   // Hold
   static readonly CALLING_HOLD = new WellKnownIconsEnum('Calling_Hold', faPauseCircleDuotone, faPauseCircleSolid);

   // Transfer
   static readonly CALLING_TRANSFER = new WellKnownIconsEnum('Calling_Transfer', faArrowAltFromLeftDuotone, faArrowAltFromLeftSolid);

   //
   // --- Emailing
   static readonly EMAILING_COMPOSE = new WellKnownIconsEnum('Emailing_Compose', faEnvelope, faEnvelope);

   // Note: always a solid icon
   static readonly EMAILING_OPEN_MESSAGE = new WellKnownIconsEnum('Emailing_Open_Message', faEnvelopeSolid, faEnvelopeSolid);

   // Note: always a solid icon
   static readonly EMAILING_REPLY = new WellKnownIconsEnum('Emailing_Reply', faReplySolid, faReplySolid);

   // Note: always a solid icon
   static readonly EMAILING_REPLY_ALL = new WellKnownIconsEnum('Emailing_Reply_All', faReplyAllSolid, faReplyAllSolid);

   // Note: always a solid icon
   static readonly EMAILING_FORWARD = new WellKnownIconsEnum('Emailing_Forward', faForwardSolid, faForwardSolid);

   // Note: always a solid icon
   static readonly EMAILING_COMPLETE = new WellKnownIconsEnum('Emailing_Complete', faCheckSolid, faCheckSolid);

   // Note: always a solid icon
   static readonly EMAILING_TRACK = new WellKnownIconsEnum('Emailing_Track', faRouteSolid, faRouteSolid);

   // Note: iconSmall is used as sub icon
   static readonly EMAILING_PARK_AGENT = new WellKnownIconsEnum('Emailing_Park_Agent', faParkingSolid, faUserSolid);

   // Note: iconSmall is used as sub icon
   static readonly EMAILING_PARK_SKILL = new WellKnownIconsEnum('Emailing_Park_Skill', faParkingSolid, faStarSolid);

   // Note: always a solid icon
   static readonly EMAILING_REQUEUE = new WellKnownIconsEnum('Emailing_Requeue', faRecycleSolid, faRecycleSolid);

   // Note: always a solid icon
   static readonly EMAILING_UNDO = new WellKnownIconsEnum('Emailing_Undo', faInboxInSolid, faInboxInSolid);

   // Note: always a solid icon
   static readonly EMAILING_DELETE = new WellKnownIconsEnum('Emailing_Delete', faTrash, faTrash);

   // Icon when user is listening
   static readonly BARGE_IN_LISTEN = new WellKnownIconsEnum('BargeInListen', faAssistiveListeningSystemsDuotone, faAssistiveListeningSystemsSolid);
   // Icon when user is whispering
   static readonly BARGE_IN_WHISPER = new WellKnownIconsEnum('BargeInWhisper', faCommentDuotone, faCommentSolid);
   // Icon when user is conferencing
   static readonly BARGE_IN_CONFERENCE = new WellKnownIconsEnum('BargeInConference', faCommentsDuotone, faCommentsSolid);
   // Icon when user is conferencing
   static readonly OPEN_CHAT = new WellKnownIconsEnum('OpenChat', faCommentsDuotone, faCommentsSolid);
   // Note: always a solid icon
   static readonly CHAT_REQUEUE = new WellKnownIconsEnum('ChatRequeue', faRecycleSolid, faRecycleSolid);

   static readonly DEFAULT_DIAL = new WellKnownIconsEnum('DefaultDial', faStarLight, faRecycleSolid);

   static readonly GREETING_FILE = new WellKnownIconsEnum('GreetingFile', faMusicAlt, faMusicAltSolid);
   static readonly GREETING_FILE_NOT_ASSIGNED = new WellKnownIconsEnum('GreetingFileNotAssigned', faMusicAltSlash, faMusicAltSlashSolid);

   private constructor(public name: string, public icon: IconDefinition, public iconSmall: IconDefinition) {
      super();
   }
}

export function getIconByExtension(fileName: string): IconDefinition {
   const extension = fileName?.substr(fileName?.lastIndexOf('.'));
   if (!extension) return faFile;

   switch (extension.toLowerCase()) {
      case '.xlsx':
      case '.xlsm':
      case '.xltx':
      case '.xltm':
      case '.xlsb':
      case '.xla':
      case '.xlam':
      case '.xll':
      case '.xlw':
         return faFileExcel;
      case '.docx':
      case '.docm':
      case '.dotx':
      case '.dotm':
      case '.docb':
         return faFileWord;
      case '.pptx':
      case '.pptm':
      case '.potx':
      case '.potm':
      case '.ppam':
      case '.ppsx':
      case '.ppsm':
      case '.sldx':
      case '.sldm':
         return faFilePowerpoint;
      case '.pdf':
         return faFilePdf;
      case '.webm':
      case '.mkv':
      case '.flv':
      case '.ogg':
      case '.ogv':
      case '.gif':
      case '.avi':
      case '.mov':
      case '.qt':
      case '.wmv':
      case '.yuv':
      case '.mp4':
      case '.m4p':
      case '.mpg':
      case '.mpeg':
      case '.mpe':
      case '.vob':
         return faFileVideo;
      case '.csv':
         return faFileSpreadsheet;
      case '.jpeg':
      case '.jpg':
      case '.png':
      case '.tiff':
      case '.psd':
      case '.eps':
      case '.ai':
      case '.indd':
      case '.raw':
      case '.bmp':
      case '.svg':
         return faFileImage;
      case '.pfx':
      case '.crt':
         return faFileCertificate;
      case '.xml':
      case '.json':
      case '.vb':
      case '.cs':
      case '.ts':
      case '.js':
      case '.cpp':
      case '.h':
      case '.ps1':
         return faFileCode;
      case '.txt':
         return faFileAlt;
      case '.wav':
      case '.mp3':
      case '.m4a':
      case '.vox':
         return faFileAudio;
      case '.zip':
      case '.7z':
      case '.rar':
      case '.tar':
         return faFileArchive;
      default:
         return faFile;
   }
}
